/**
 * Brand Assets Helper
 *
 * Provides access to subsidiary-specific brand assets (logos, favicons).
 * Assets are located in /public/brands/{subsidiary}/ directory.
 *
 * Usage:
 *   import { getBrandAsset } from '@/themes';
 *   <img src={getBrandAsset('logo.svg')} alt="Logo" />
 */

import { SUBSIDIARY_CODE } from '@/config';

/**
 * Returns the path to a brand asset for the current subsidiary.
 *
 * @param filename - The asset filename (e.g., 'logo.svg', 'logo-small.svg', 'favicon.svg')
 * @returns The full path to the asset (e.g., '/brands/nonpartisan/logo.svg')
 */
export function getBrandAsset(filename: string): string {
  // GOP uses a PNG logo instead of SVG
  if (SUBSIDIARY_CODE === 'gop' && filename === 'logo.svg') {
    return `/brands/gop/logo.png`;
  }
  return `/brands/${SUBSIDIARY_CODE}/${filename}`;
}

/**
 * Common brand asset paths for convenience.
 */
export const brandAssets = {
  /** Full logo with text */
  logo: getBrandAsset('logo.svg'),
  /** Small logo variant (icon only) */
  logoSmall: getBrandAsset('logo-small.svg'),
  /** Favicon */
  favicon: getBrandAsset('favicon.svg'),
  /** Open Graph image for landing/social sharing */
  ogImage: getBrandAsset('landing/og-image.svg'),
} as const;
